import React, { useContext, useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { TbMotorbike } from 'react-icons/tb'
import { Redirect, RouteComponentProps, Switch, withRouter } from 'react-router-dom'

import api from 'src/api'
import { DomainPermission_DomainEnum, DomainPermission_VerbEnum } from 'src/api/gen'
import { AreaResponse, Favorite_PageIdEnum, HopOnVehicle } from 'src/api/gen/api'
import { lexicographicSort } from 'src/commons/helpers'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { userHasPermission } from 'src/commons/permissions'
import { getTemporalRange, ZonedDateTimeToJSON } from 'src/commons/temporalHelpers'
import { CardTabHeader } from 'src/components/Card'
import Header from 'src/layout/Header'
import RestrictedRoute from 'src/layout/RestrictedRoute'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'

import FilterPanel, { IHopOnFilters } from './FilterPanel'
import VehiclesList from './VehiclesList'
import VehiclesMap from './VehiclesMap'

export function getInitialFilters(): IHopOnFilters {
  const initialFilters: IHopOnFilters = {
    dateRange: getTemporalRange({ days: 7 }, true),
    status: new Set(),
    zones: [],
  }
  return initialFilters
}

export const HopOnFiltersContext = React.createContext<IHopOnFilters>(getInitialFilters())
export const useFilters = (): IHopOnFilters => useContext(HopOnFiltersContext)

function HopOn(props: RouteComponentProps) {
  const { t } = useTranslation()

  const [vehicles, setVehicles] = useState<HopOnVehicle[]>([])
  const [vehiclesCount, setVehiclesCount] = useState(0)
  const [isLoading, setLoading] = useState(false)
  const [areas, setAreas] = useState<AreaResponse[]>([])

  const defaultFilters = useMemo(() => getInitialFilters(), [])
  const [filters, setFilters] = useURLAndStorageState(Favorite_PageIdEnum.hopon, defaultFilters)
  const displayedFiltersRef = useRef<IHopOnFilters>()

  const onApplyFilter = function () {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters(displayedFiltersRef.current)
  }

  const vehicleMapHopOnURL = `${props.match.url}/map`
  const vehicleListHopOnURL = `${props.match.url}/list`

  const tabs = []

  if (userHasPermission([DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.device])) {
    tabs.push({
      id: tabs.length,
      label: t('hopon.map.title'),
      to: vehicleMapHopOnURL,
    })
    tabs.push({
      id: tabs.length,
      label: t('hopon.list.title'),
      to: vehicleListHopOnURL,
    })
  }

  const fetchDataVehicles = async function () {
    setLoading(true)
    try {
      const response = await api.hopon.hoponList({
        status: Array.from(filters.status),
        updatedAt_after: filters.dateRange.from ? ZonedDateTimeToJSON(filters.dateRange.from) : undefined,
        updatedAt_before: filters.dateRange.to ? ZonedDateTimeToJSON(filters.dateRange.to) : undefined,
      })
      const vehicles = response.results
      setVehicles(vehicles)
      setVehiclesCount(vehicles.length)
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  const fetchArea = async () => {
    const fetchedAreas = await api.areas.serviceAreasList()
    setAreas(lexicographicSort(fetchedAreas || []))
  }

  useEffect(() => {
    try {
      fetchArea()
    } catch (e) {
      console.error('Cannot fetch service areas;')
    }
  }, [])

  useEffect(() => {
    fetchDataVehicles()
  }, [filters])

  return (
    <>
      <Header title="Hop On" icon={TbMotorbike}>
        <CardTabHeader entries={tabs} />
      </Header>
      <FilteringCard
        onApplyFilter={onApplyFilter}
        SideComponent={
          <FilterPanel
            ref={displayedFiltersRef}
            initialFilters={filters}
            defaultFilters={defaultFilters}
            areaLabels={areas.map((area) => area.label)}
            vehiclesCount={vehiclesCount}
          />
        }
      >
        <HopOnFiltersContext.Provider value={filters}>
          <Switch>
            <RestrictedRoute
              path={vehicleListHopOnURL}
              render={() => <VehiclesList isLoading={isLoading} vehicles={vehicles} vehiclesCount={vehiclesCount} />}
              requiredPermissionsUnion={[[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.device]]}
            />
            <RestrictedRoute
              path={vehicleMapHopOnURL}
              render={() => (
                <VehiclesMap
                  isLoading={isLoading}
                  vehicles={vehicles}
                  areas={areas.filter((area) => filters.zones.includes(area.label))}
                />
              )}
              requiredPermissionsUnion={[[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.device]]}
            />
            <Redirect to={vehicleMapHopOnURL} />
          </Switch>
        </HopOnFiltersContext.Provider>
      </FilteringCard>
    </>
  )
}

export default withRouter(HopOn)
